import React from 'react';
import { ITheme } from '@klangio/react-shared-elements';
import { makeStyles } from 'tss-react/mui';
import { useMediaQuery, Theme } from '@mui/material';

function calcOldPrice(price: string, discount: number) {
  const myRegexp = /(?:[^\d,]|^)(\d+(?:(?:,\d+)*,\d{3})?(\.\d+)?)\b/g;

  // @ts-ignore: Object possibly null
  const num = myRegexp.exec(price)[1];
  const perItemPrice = parseFloat(num.replace(',', '')) / (1 - discount);
  if (num.includes('.')) {
    return price.replace(num, `${perItemPrice.toFixed(0)}.99`);
  }
  return price.replace(num, perItemPrice.toFixed(0));
}

const useStyles = makeStyles()((theme: ITheme) => ({
  ecardsItem: {
    cursor: 'pointer',
    backgroundColor: '#FFF',
    borderRadius: 10,
    height: 259,
    paddingTop: 20,
    boxShadow:
      '0px 3px 3px -2px rgb(0 0 0 / 20%), 0px 3px 4px 0px rgb(0 0 0 / 14%), 0px 1px 8px 0px rgb(0 0 0 / 12%)',
    '-webkit-transform': 'translateZ(0)',
    transform: 'translateZ(0)',

    [theme.breakpoints.down('md')]: {
      width: '100%',
      height: 98,
      marginBottom: 10,
      borderRadius: 10,
      display: 'grid',
      gridTemplateColumns: '1fr 4fr 1fr',
      alignItems: 'center',
      justifyContent: 'space-around',
      gap: 10,
      padding: 10,
    },
    [theme.breakpoints.up('md')]: {
      '-webkit-transition-property': '-webkit-transform',
      'transition-property': 'transform, -webkit-transform',
      '-webkit-transition-duration': '300ms',
      'transition-duration': '300ms',
      '&:hover': {
        '-webkit-transform': 'scale(1.05)',
        '-ms-transform': 'scale(1.05)',
        transform: 'scale(1.05)',
      },
    },
  },
  period: {
    margin: 'auto',
    width: 150,
    height: 150,
    borderRadius: '50%',
    textAlign: 'center',
    backgroundColor: '#FFF',
    fontSize: 28,
    marginBottom: 9,
    [theme.breakpoints.down('md')]: {
      width: 55,
      height: 55,
      margin: 0,
      // margin: '8px 0 0 5%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  amount: {
    fontSize: 56,
    fontWeight: 700,
    lineHeight: '65px',
    paddingTop: 30,
    [theme.breakpoints.down('md')]: {
      paddingTop: 0,
      fontSize: 28,
    },
  },
  unit: {
    fontSize: 16,
    fontWeight: 700,
    [theme.breakpoints.down('md')]: {
      fontSize: 18,
      fontWeight: 400,
      color: '#FFF',
    },
  },
  price: {
    paddingTop: 20,
    [theme.breakpoints.down('md')]: {
      fontSize: 18,
      color: '#FFF',
      padding: 0,
    },
    [theme.breakpoints.up('md')]: {
      height: 80,
      backgroundColor: '#FFF',
      borderBottomLeftRadius: 10,
      borderBottomRightRadius: 10,
      textAlign: 'center',
      width: '100%',
    },
  },
  priceDiscounted: {
    paddingTop: 14,
    [theme.breakpoints.down('md')]: {
      fontSize: 18,
      color: '#FFF',
      textAlign: 'right',
    },
    [theme.breakpoints.up('md')]: {
      height: 110,
      backgroundColor: '#FFF',
      borderBottomLeftRadius: 10,
      borderBottomRightRadius: 10,
      textAlign: 'center',
      width: '100%',
    },
  },
  priceText: {
    fontWeight: 700,
    textTransform: 'capitalize',
    fontSize: 28,
    [theme.breakpoints.down('md')]: {
      fontSize: 18,
      color: '#FFF',
    },
  },
  sale: {
    fontWeight: 'bold',
    fontSize: 14,
    lineHeight: '15px',
    color: '#e60000',
    display: 'block',
    marginBottom: 6,
    padding: 3,
    borderRadius: 3,
    backgroundColor: 'rgba(255,255,255,0.85)',
  },
  priceBefore: {
    fontSize: 16,
    lineHeight: '16px',
    textDecorationLine: 'line-through',
    color: '#979797',
    display: 'block',
    [theme.breakpoints.down('md')]: {
      fontSize: 14,
      lineHeight: '14px',
      color: '#CCC',
    },
  },
}));

const COLORS: any = {
  green: {
    color: '#006450',
    background:
      'linear-gradient(45deg, #106f58 25%, #016450 25%, #016450 50%, #106f58 50%, #106f58 75%, #016450 75%, #016450 100%)',
  },
  yellow: {
    color: '#FFC864',
    background:
      'linear-gradient(45deg, #f59311 25%, #f59b23 25%, #f59b23 50%, #f59311 50%, #f59311 75%, #f59b23 75%, #f59b23 100%)',
  },
  blue: {
    color: '#1E3264',
    background:
      'linear-gradient(45deg, #243883 25%, #1e3264 25%, #1e3264 50%, #243883 50%, #243883 75%, #1e3264 75%, #1e3264 100%)',
  },
  pink: {
    color: '#F573A0',
    background:
      'linear-gradient(45deg, #f46697 25%, #f573a0 25%, #f573a0 50%, #f46697 50%, #f46697 75%, #f573a0 75%, #f573a0 100%)',
  },
};

export interface EcardsAmountItemProps {
  amount: number;
  price: string;
  color: string;
  selected: boolean;
  discount: number;
  onClick: () => void;
}

const EcardsAmountItem: React.FC<EcardsAmountItemProps> = ({
  amount,
  price,
  color,
  selected,
  discount = undefined,
  onClick = undefined,
}) => {
  const { classes } = useStyles();

  const oldPrice = discount && calcOldPrice(price, discount);
  const smallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <div
      role="button"
      tabIndex={-1}
      onKeyDown={onClick}
      className={classes.ecardsItem}
      style={{
        color: COLORS[color].color,
        backgroundImage: COLORS[color].background,
        border: selected && 'solid #ffffff',
        scale: selected && '1.05',
      }}
      onClick={onClick}
    >
      <div className={classes.period}>
        <div className={classes.amount}>{amount}</div>
        {!smallScreen && <div className={classes.unit}>Transcriptions</div>}
      </div>
      {smallScreen && <div className={classes.unit}>Transcriptions</div>}

      <div className={discount ? classes.priceDiscounted : classes.price}>
        {discount ? (
          <>
            <span className={classes.sale}>🎁 Holiday Sale!</span>
            <span className={classes.priceBefore}>
              {' '}
              {oldPrice}
              {' '}
            </span>
            {' '}
          </>
        ) : null}
        <span className={classes.priceText}>{price}</span>
      </div>
    </div>
  );
};

export default EcardsAmountItem;
